import { useCallback, useEffect, useMemo, useState } from 'react';
import { AggregateLiqState } from 'src/hooks/Liquidations/useAggregateLiquidationDataForUser';
import useAggregateLiquidations from 'src/hooks/Liquidations/useAggregateLiquidations';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import useLiquidationsTableColumns from 'src/components/LiquidationsTable/hooks/useLiquidationsTableColumns';
import { useLiquidationsHistory } from 'src/hooks/historyData/useLiquidationsHistory';

const PAGE_SIZE = 20;

export default function useUserLiquidationsTable() {
	const { executePageChange: onPageChange, historyData } =
		useLiquidationsHistory();

	const { toggleDateDisplayType, displayDatesAsTimestamp } =
		useDateDisplayState();

	const pageIndex = useHistoryTableFilterStore(
		(s) => s.getFilterState('liquidations').currentUiPage
	);
	const setHistoryTableFilterStore = useHistoryTableFilterStore((s) => s.set);

	const liquidations = useAggregateLiquidations(historyData.records);

	const [visibleRecords, setVisibleRecords] = useState(
		liquidations.slice(0, PAGE_SIZE)
	);

	useEffect(() => {
		setVisibleRecords(
			liquidations.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE)
		);
	}, [liquidations, pageIndex]);

	const resetState = useCallback(() => {
		setHistoryTableFilterStore((s) => {
			s.liquidations.currentUiPage = 0;
		});
	}, [setHistoryTableFilterStore]);

	useWalletIsConnected({ onDisconnectedCallback: resetState });

	const setDrawersStore = useDrawersStore((s) => s.set);

	const showLiquidationDetailsDrawer = useCallback(
		(liq: AggregateLiqState) => {
			setDrawersStore((s) => {
				s.liquidations.liquidation = liq;
				s.liquidations.show = true;
			});
		},
		[setDrawersStore]
	);

	const pagination = useMemo(
		() => ({
			onPageChange,
			pageIndex,
			pageSize: PAGE_SIZE,
			totalCount: historyData.totalCount,
		}),
		[onPageChange, pageIndex, historyData.totalCount]
	);

	const columns = useLiquidationsTableColumns({
		toggleDateDisplayType,
		displayDatesAsTimestamp,
	});

	const isLoading = historyData.loading;

	return {
		data: visibleRecords,
		columns,
		toggleDateDisplayType,
		displayDatesAsTimestamp,
		showLiquidationDetailsDrawer,
		pagination,
		isLoading,
	};
}
